import React, { useEffect, useRef, useState } from 'react';
import type { QuickMessageContent, QuickMessageResponse, QuickMessageScope } from '@troia-v3/types';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { Button, FormField, Input, Textarea, Tooltip } from '@/shared/components/ui';
import { useAudioRecording } from '@/modules/chat/hooks/useAudioRecording';

import { useCreateQuickMessage, useUpdateQuickMessage } from '../hooks/useQuickMessages';

interface QuickMessageFormProps {
  /** Scope of the message (set by the active drawer tab; immutable on edit). */
  scope: QuickMessageScope;
  /** Existing message to edit (null = create). */
  editing?: QuickMessageResponse | null;
  onCancel: () => void;
  onSaved: () => void;
}

interface UploadResult {
  url: string;
  mimeType: string;
  size: number;
  originalName: string;
}

const uploadToStorage = async (file: File): Promise<UploadResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('uploads?folder=quick-messages', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const data = response.data.data as {
    url: string;
    mimeType?: string;
    size?: number;
    originalName?: string;
  };
  return {
    url: data.url,
    mimeType: data.mimeType ?? file.type,
    size: data.size ?? file.size,
    originalName: data.originalName ?? file.name,
  };
};

const buildMediaContent = (result: UploadResult, duration?: number): QuickMessageContent => {
  const { url, mimeType, size, originalName } = result;
  if (mimeType.startsWith('image/')) {
    return { type: 'image', url, size, mimeType };
  }
  if (mimeType.startsWith('video/')) {
    return { type: 'video', url, size, mimeType };
  }
  if (mimeType.startsWith('audio/')) {
    return { type: 'audio', url, size, mimeType, ...(duration ? { duration } : {}) };
  }
  return { type: 'document', url, filename: originalName, size, mimeType };
};

const extractText = (content: QuickMessageContent[]): string => {
  const part = content.find((c) => c.type === 'text');
  return part && part.type === 'text' ? part.text : '';
};

const extractMedia = (content: QuickMessageContent[]): QuickMessageContent | null =>
  content.find((c) => c.type !== 'text') ?? null;

const mediaLabel = (media: QuickMessageContent): string => {
  switch (media.type) {
    case 'image':
      return 'Imagem anexada';
    case 'video':
      return 'Vídeo anexado';
    case 'audio':
      return 'Áudio';
    case 'document':
      return media.filename;
    default:
      return 'Anexo';
  }
};

const mediaIconName = (media: QuickMessageContent): IconName => {
  switch (media.type) {
    case 'image':
      return 'image';
    case 'video':
      return 'video';
    case 'audio':
      return 'music';
    default:
      return 'file';
  }
};

/**
 * Create/edit form for a quick message — supports the 4 chat modes
 * (text, attachment, text+attachment, audio). Media is uploaded to S3 on save.
 */
const QuickMessageForm: React.FC<QuickMessageFormProps> = ({ scope, editing, onCancel, onSaved }) => {
  const [title, setTitle] = useState('');
  const [shortcut, setShortcut] = useState('');
  const [text, setText] = useState('');
  const [media, setMedia] = useState<QuickMessageContent | null>(null);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const createMutation = useCreateQuickMessage();
  const updateMutation = useUpdateQuickMessage();

  const {
    isRecording,
    duration,
    audioData,
    startRecording,
    stopRecording,
    cancelRecording,
    clearAudio,
    formatDuration,
  } = useAudioRecording({
    onRecordingComplete: () => {
      /* upload happens on stop via the effect below */
    },
  });

  // Prefill when editing.
  useEffect(() => {
    if (editing) {
      setTitle(editing.title);
      setShortcut(editing.shortcut ?? '');
      setText(extractText(editing.content));
      setMedia(extractMedia(editing.content));
    } else {
      setTitle('');
      setShortcut('');
      setText('');
      setMedia(null);
    }
  }, [editing]);

  // When a recording finishes, upload the blob and stage it as audio media.
  useEffect(() => {
    if (!audioData) return;
    let cancelled = false;
    void (async () => {
      setUploading(true);
      try {
        const audioFile = new File([audioData.blob], `audio_${Date.now()}.ogg`, {
          type: audioData.blob.type || 'audio/ogg',
        });
        const result = await uploadToStorage(audioFile);
        if (!cancelled) {
          setMedia(buildMediaContent(result, audioData.duration));
        }
      } catch {
        if (!cancelled) toast.error('Erro ao enviar áudio');
      } finally {
        if (!cancelled) {
          setUploading(false);
          clearAudio();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [audioData, clearAudio]);

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadToStorage(file);
      setMedia(buildMediaContent(result));
    } catch {
      toast.error('Erro ao enviar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Informe um título');
      return;
    }

    const content: QuickMessageContent[] = [
      ...(media ? [media] : []),
      ...(text.trim() ? [{ type: 'text' as const, text: text.trim() }] : []),
    ];

    if (content.length === 0) {
      toast.error('Adicione um texto, anexo ou áudio');
      return;
    }

    try {
      if (editing) {
        await updateMutation.mutateAsync({
          id: editing.id,
          data: { title: title.trim(), shortcut: shortcut.trim() || undefined, content },
        });
        toast.success('Mensagem rápida atualizada');
      } else {
        await createMutation.mutateAsync({
          scope,
          title: title.trim(),
          shortcut: shortcut.trim() || undefined,
          content,
        });
        toast.success('Mensagem rápida criada');
      }
      onSaved();
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ??
            'Erro ao salvar')
          : 'Erro ao salvar';
      toast.error(message);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-6 space-y-5" data-testid="quick-message-form">
      <FormField label="Título" required>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex: Saudação inicial"
          data-testid="quick-message-form-title"
        />
      </FormField>

      <FormField label="Atalho" description="Usado ao digitar / no chat (ex: /ola)">
        <Input
          value={shortcut}
          onChange={(e) => setShortcut(e.target.value)}
          placeholder="ola"
          data-testid="quick-message-form-shortcut"
        />
      </FormField>

      <Textarea
        label="Mensagem"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Texto da mensagem rápida"
        rows={5}
        data-testid="quick-message-form-text"
      />

      {/* Media / audio */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
          Anexo ou áudio
        </label>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileChange}
          className="hidden"
        />

        {media ? (
          <div
            className="flex items-center gap-3 p-3 rounded-lg border"
            style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb' }}
          >
            {media.type === 'image' ? (
              <img src={media.url} alt="" className="w-12 h-12 rounded object-cover" />
            ) : (
              <div
                className="w-12 h-12 rounded flex items-center justify-center"
                style={{ backgroundColor: '#ede9fe' }}
              >
                <Icon name={mediaIconName(media)} className="w-6 h-6" style={{ color: '#8b5cf6' }} />
              </div>
            )}
            <span className="flex-1 text-sm truncate" style={{ color: '#334155' }}>
              {mediaLabel(media)}
            </span>
            <Tooltip content="Remover">
              <button
                type="button"
                onClick={() => setMedia(null)}
                className="p-1.5 rounded-md"
                style={{ color: '#64748b' }}
                data-testid="quick-message-form-remove-media"
              >
                <Icon name="trash" className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>
        ) : isRecording ? (
          <div
            className="flex items-center gap-3 p-3 rounded-lg border"
            style={{ borderColor: '#fecaca', backgroundColor: '#fef2f2' }}
          >
            <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: '#ef4444' }} />
            <span className="flex-1 text-sm" style={{ color: '#991b1b' }}>
              Gravando… {formatDuration(duration)}
            </span>
            <Button variant="secondary" size="sm" onClick={cancelRecording} data-testid="quick-message-form-cancel-audio">
              Cancelar
            </Button>
            <Button variant="primary" size="sm" onClick={stopRecording} data-testid="quick-message-form-stop-audio">
              Concluir
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              icon="paperclip"
              onClick={handleAttachClick}
              loading={uploading}
              disabled={uploading}
              data-testid="quick-message-form-attach"
            >
              Anexar arquivo
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon="mic"
              onClick={startRecording}
              disabled={uploading}
              data-testid="quick-message-form-record"
            >
              Gravar áudio
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button variant="ghost" onClick={onCancel} disabled={isSaving} data-testid="quick-message-form-cancel">
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || uploading}
          data-testid="quick-message-form-save"
        >
          {editing ? 'Salvar' : 'Criar'}
        </Button>
      </div>
    </div>
  );
};

QuickMessageForm.displayName = 'QuickMessageForm';

export default QuickMessageForm;
